// Hinweis-Dialog mit „OK“ für mehrteilige Meldungen, die ein Toast nicht fassen würde
// (z. B. „Protokoll abgeschlossen.“ + Abschlusshinweise). Absätze werden an Leerzeilen getrennt.
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, WarningCircle, type Icon } from '@phosphor-icons/react';
import { useDialogFokus } from '../immobilien/ui/fokus';

export interface MeldungDialogProps {
  /** Meldungstext; Absätze durch „\n\n“ getrennt, einfache Umbrüche bleiben erhalten */
  text: string;
  fehler?: boolean;
  onSchliessen: () => void;
}

export function MeldungDialog({ text, fehler = false, onSchliessen }: MeldungDialogProps) {
  useDialogFokus<HTMLDivElement>();
  const ok = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    ok.current?.focus();
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' || e.key === 'Enter') onSchliessen(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onSchliessen]);
  const [titel, ...rest] = text.split('\n\n');
  const I: Icon = fehler ? WarningCircle : CheckCircle;
  const farbe = fehler ? 'var(--bad)' : 'var(--ok)';

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 450, background: 'rgba(0,0,0,0.35)', fontFamily: "'Manrope', -apple-system, sans-serif" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onSchliessen(); }}>
      <div role="alertdialog" aria-modal="true" aria-labelledby="immo-meldung-titel"
        className="immo-glas-stark immo-side-in flex w-full flex-col" style={{ maxWidth: 440, borderRadius: 26, padding: 22, gap: 14, color: 'var(--ink)' }}>
        <div className="flex items-start" style={{ gap: 12 }}>
          <span className="flex shrink-0 items-center justify-center" style={{ width: 42, height: 42, borderRadius: 14, background: `color-mix(in srgb, ${farbe} 14%, transparent)` }}>
            <I weight="fill" size={20} color={farbe} />
          </span>
          <span id="immo-meldung-titel" className="whitespace-pre-line" style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.35, paddingTop: 10 }}>{titel}</span>
        </div>
        {rest.length > 0 && (
          <div className="immo-innen flex flex-col" style={{ borderRadius: 16, padding: '12px 14px', gap: 8, fontSize: 13, lineHeight: 1.45 }}>
            {rest.map((absatz, i) => <p key={i} className="m-0 whitespace-pre-line">{absatz}</p>)}
          </div>
        )}
        <button ref={ok} type="button" onClick={onSchliessen}
          style={{ height: 44, borderRadius: 14, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 800,
            background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' }}>
          OK
        </button>
      </div>
    </div>,
    document.body,
  );
}
